import { Button } from "@kopi/ui/components/button";
import { Card, CardContent } from "@kopi/ui/components/card";
import { Input } from "@kopi/ui/components/input";
import { Label } from "@kopi/ui/components/label";
import { DEFAULT_BRAND } from "@kopi/ui/lib/brand";
import { cn } from "@kopi/ui/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  AlertCircleIcon,
  ArrowLeftIcon,
  CheckCircle2Icon,
  EyeIcon,
  EyeOffIcon,
  KeyRoundIcon,
  LockIcon,
  MailIcon,
  ShieldCheckIcon,
  UserCheckIcon,
} from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import { TenantLogo, useDocumentBrand } from "@/components/brand";
import { LanguagePicker } from "@/components/language";
import { PinPad } from "@/components/pin-pad";
import { ThemePicker } from "@/components/theme";
import { login } from "@/i18n/messages/login";
import { useMessages } from "@/i18n/provider";
import type { PlainKeyOf } from "@/i18n/translate";
import { ApiRequestError, api, isUnauthenticated, unwrap } from "@/lib/api";
import { meScopesQuery, publicBrandQuery, registerStaffQuery } from "@/lib/queries";
import { safeRedirect } from "@/lib/safe-redirect";

type LoginKey = PlainKeyOf<typeof login.id>;

interface LoginSearch {
  redirect?: string;
}

export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>): LoginSearch => {
    const redirect = safeRedirect(search.redirect);
    return redirect ? { redirect } : {};
  },
  component: LoginPage,
});

function LoginPage() {
  const { t } = useMessages(login);
  const { redirect } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [challengeToken, setChallengeToken] = useState<string>();
  // On an enrolled register, staff unlock with their PIN; elsewhere it's email + password.
  const registerStaff = useQuery(registerStaffQuery);
  const [usePassword, setUsePassword] = useState(false);
  const onRegister = registerStaff.isSuccess && !usePassword;

  async function signedIn() {
    await queryClient.invalidateQueries({ queryKey: meScopesQuery.queryKey });
    await navigate({ to: redirect ?? "/" });
  }

  // An enrolled register shows its tenant's brand; anywhere else, the defaults (nothing leaks).
  const brand = useQuery(publicBrandQuery).data ?? DEFAULT_BRAND;
  useDocumentBrand(brand, t("login.title"));
  const refusal = deviceRefusal(registerStaff.error);

  return (
    <main className="relative flex min-h-dvh w-full flex-col justify-between overflow-x-hidden bg-background px-4 py-8 sm:px-6 lg:px-8">
      {/* Background ambient subtle coffee tint highlights */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 flex items-center justify-center overflow-hidden"
      >
        <div className="size-[32rem] rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute -top-24 size-[24rem] rounded-full bg-secondary/5 blur-3xl" />
      </div>

      {/* Top action bar: brand lockup, language & theme */}
      <header className="mx-auto flex w-full max-w-5xl items-center justify-between pt-[env(safe-area-inset-top)]">
        <TenantLogo brand={brand} size={32} />
        <div className="flex items-center gap-2">
          <LanguagePicker />
          <ThemePicker />
        </div>
      </header>

      {/* Center login card */}
      <div className="my-auto flex w-full flex-col items-center justify-center py-6">
        <Card className="w-full max-w-[420px] border-border/80 bg-card/90 shadow-xl backdrop-blur-sm sm:rounded-2xl">
          <CardContent className="flex flex-col gap-6 p-6 sm:p-8">
            <div className="flex flex-col items-center text-center">
              <div className="mb-4 flex size-14 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary shadow-xs">
                {challengeToken ? (
                  <ShieldCheckIcon className="size-7" />
                ) : onRegister ? (
                  <UserCheckIcon className="size-7" />
                ) : (
                  <LockIcon className="size-7" />
                )}
              </div>
              <h1 className="font-semibold text-2xl text-foreground tracking-tight">{t("login.title")}</h1>
              <p className="mt-1.5 text-muted-foreground text-sm leading-relaxed">
                {t(
                  challengeToken
                    ? "login.withAuthenticator"
                    : onRegister
                      ? "login.withPin"
                      : "login.withPassword",
                )}
              </p>
              {refusal && <FormAlert wide message={refusal} />}
            </div>

            {challengeToken ? (
              <SecondFactorStep
                challengeToken={challengeToken}
                onSignedIn={signedIn}
                onRestart={() => setChallengeToken(undefined)}
              />
            ) : onRegister ? (
              <PinStep staff={registerStaff.data} onSignedIn={signedIn} />
            ) : (
              <PasswordStep onSignedIn={signedIn} onChallenge={setChallengeToken} />
            )}

            {registerStaff.isSuccess && !challengeToken && (
              <div className="pt-2">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-center text-xs"
                  onClick={() => setUsePassword((v) => !v)}
                >
                  <KeyRoundIcon className="mr-1.5 size-3.5" />
                  {t(usePassword ? "login.usePin" : "login.useEmail")}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div aria-hidden="true" className="h-4" />
    </main>
  );
}

function PasswordStep(props: { onSignedIn: () => Promise<void>; onChallenge: (token: string) => void }) {
  const { t } = useMessages(login);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const emailId = useId();
  const passwordId = useId();

  const signIn = useMutation({
    mutationFn: async () => unwrap(await api.v1.auth.sessions.post({ email, password })),
    onSuccess: async (result) => {
      if (result.status === "mfa_required") props.onChallenge(result.challengeToken);
      else await props.onSignedIn();
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    signIn.mutate();
  }

  const error = signIn.error;
  const message: LoginKey | undefined =
    error instanceof ApiRequestError && error.code === "device_required"
      ? "login.deviceRequired"
      : (deviceRefusal(error) ??
        (isUnauthenticated(error) ? "login.wrongPassword" : error ? "login.failed" : undefined));

  return (
    <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={emailId}>{t("login.email")}</Label>
        <div className="relative">
          <Input
            id={emailId}
            type="email"
            autoComplete="username"
            inputMode="email"
            enterKeyHint="next"
            required
            className="pl-10"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <MailIcon className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={passwordId}>{t("login.password")}</Label>
        <div className="relative">
          <Input
            id={passwordId}
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            enterKeyHint="go"
            required
            className="pr-10 pl-10"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <LockIcon className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute top-1/2 right-0.5 size-10 -translate-y-1/2 rounded-r-md text-muted-foreground hover:text-foreground"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={t(showPassword ? "login.hidePassword" : "login.showPassword")}
          >
            {showPassword ? <EyeOffIcon className="size-4" /> : <EyeIcon className="size-4" />}
          </Button>
        </div>
      </div>
      {message && <FormAlert message={message} />}
      <Button
        type="submit"
        size="pos"
        className="mt-1 shadow-sm transition-transform active:scale-[0.99]"
        disabled={signIn.isPending || !email || !password}
      >
        {t(signIn.isPending ? "login.submitting" : "login.submit")}
      </Button>
    </form>
  );
}

/** A sign-in error: the problem and the next safe action, styled consistently across steps. */
function FormAlert(props: { message: LoginKey; wide?: boolean }) {
  const { t } = useMessages(login);
  return (
    <div
      role="alert"
      className={cn(
        "rounded-lg border border-destructive/20 bg-destructive/10 p-3 text-destructive text-sm",
        props.wide ? "mt-4 flex w-full items-start gap-2.5 text-left" : "flex items-center gap-2",
      )}
    >
      <AlertCircleIcon className="mt-0.5 size-4 shrink-0" />
      <span>{t(props.message)}</span>
    </div>
  );
}

/** Why this register can't be used, if the API said so (R1-07/R1-08). */
function deviceRefusal(error: unknown): LoginKey | undefined {
  if (!(error instanceof ApiRequestError)) return undefined;
  if (error.code === "outlet_inactive") return "login.outletInactive";
  if (error.code === "device_revoked") return "login.deviceRevoked";
  return undefined;
}

function PinStep(props: {
  staff: { employeeId: string; displayName: string }[];
  onSignedIn: () => Promise<void>;
}) {
  const { t } = useMessages(login);
  const [employeeId, setEmployeeId] = useState<string>();
  const [pin, setPin] = useState("");
  const selected = props.staff.find((s) => s.employeeId === employeeId);

  const unlock = useMutation({
    mutationFn: async () =>
      unwrap(await api.v1.auth["pin-sessions"].post({ employeeId: employeeId ?? "", pin })),
    onSuccess: props.onSignedIn,
    onError: () => setPin(""),
  });

  const error = unlock.error;
  const message: LoginKey | undefined = !error
    ? undefined
    : error instanceof ApiRequestError && error.code === "pin_locked"
      ? "login.pinLocked"
      : (deviceRefusal(error) ?? (isUnauthenticated(error) ? "login.wrongPin" : "login.failed"));

  if (!selected) {
    if (props.staff.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border/80 p-6 text-center">
          <UserCheckIcon className="mb-2 size-8 text-muted-foreground/50" />
          <p className="text-muted-foreground text-sm">{t("login.noPinStaff")}</p>
        </div>
      );
    }
    return (
      <ul className="grid grid-cols-2 gap-3">
        {props.staff.map((s) => (
          <li key={s.employeeId}>
            <Button
              type="button"
              variant="outline"
              size="pos"
              className="w-full flex-col items-center justify-center gap-1 rounded-xl border-border/80 bg-background/80 py-3 text-center shadow-xs transition-all hover:border-primary/40 hover:bg-accent/40 active:scale-[0.98]"
              onClick={() => setEmployeeId(s.employeeId)}
            >
              <span className="max-w-full truncate font-medium text-foreground text-sm">{s.displayName}</span>
            </Button>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between rounded-xl border border-border/80 bg-muted/30 px-3.5 py-2">
        <div className="flex items-center gap-2 overflow-hidden">
          <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <CheckCircle2Icon className="size-4" />
          </div>
          <p className="truncate font-semibold text-foreground text-sm">{selected.displayName}</p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 gap-1 px-2.5 text-muted-foreground text-xs hover:text-foreground"
          onClick={() => {
            setEmployeeId(undefined);
            setPin("");
            unlock.reset();
          }}
        >
          <ArrowLeftIcon className="size-3.5" />
          {t("login.changeName")}
        </Button>
      </div>
      <PinPad value={pin} onChange={setPin} disabled={unlock.isPending} onSubmit={() => unlock.mutate()} />
      {message && <FormAlert message={message} />}
    </div>
  );
}

function SecondFactorStep(props: {
  challengeToken: string;
  onSignedIn: () => Promise<void>;
  onRestart: () => void;
}) {
  const { t } = useMessages(login);
  const [useRecovery, setUseRecovery] = useState(false);
  const [value, setValue] = useState("");
  const inputId = useId();

  const verify = useMutation({
    mutationFn: async () => {
      const factor = useRecovery ? { recoveryCode: value.trim() } : { code: value.trim() };
      return unwrap(await api.v1.auth.sessions.mfa.post({ challengeToken: props.challengeToken, ...factor }));
    },
    onSuccess: props.onSignedIn,
    onError: () => setValue(""),
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    verify.mutate();
  }

  const error = verify.error;
  const expired = error instanceof ApiRequestError && error.code === "invalid_challenge";
  const message: LoginKey | undefined = !error
    ? undefined
    : error instanceof ApiRequestError && error.status === 429
      ? "login.codeLocked"
      : expired
        ? "login.challengeExpired"
        : isUnauthenticated(error)
          ? "login.wrongCode"
          : "login.verifyFailed";

  return (
    <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={inputId}>{t(useRecovery ? "login.recoveryCode" : "login.authenticatorCode")}</Label>
        <Input
          id={inputId}
          autoFocus
          autoComplete="one-time-code"
          inputMode={useRecovery ? "text" : "numeric"}
          pattern={useRecovery ? undefined : "[0-9]*"}
          maxLength={useRecovery ? 24 : 6}
          enterKeyHint="go"
          className="text-center font-mono text-lg tracking-widest shadow-inner"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
      </div>
      {message && <FormAlert message={message} />}
      {expired ? (
        <Button type="button" size="pos" className="shadow-sm" onClick={props.onRestart}>
          {t("login.restart")}
        </Button>
      ) : (
        <Button
          type="submit"
          size="pos"
          className="shadow-sm transition-transform active:scale-[0.99]"
          disabled={verify.isPending || value.trim().length < 6}
        >
          {t(verify.isPending ? "login.verifying" : "login.verify")}
        </Button>
      )}
      <Button
        type="button"
        variant="ghost"
        className="text-xs"
        onClick={() => {
          setUseRecovery((v) => !v);
          setValue("");
          verify.reset();
        }}
      >
        {t(useRecovery ? "login.useAuthenticator" : "login.useRecovery")}
      </Button>
    </form>
  );
}
